import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgStack } from '@/hooks/ceox/useOrgStack';
import { LoadingState } from '@/components/shared';
import { StackCategory, StackProvider, STACK_CATEGORIES } from '@/types/api/ceoxStack';
import { Paths } from '@/types/routes';
import CEOxStackSetupComponent from './CEOxStackSetup.component';

const CEOxStackSetupContainer = function (): JSX.Element {
  const navigate = useNavigate();
  const { stack, isLoading, hasStack, saveStack } = useOrgStack();
  const [isSaving, setIsSaving] = useState(false);

  // Initialize selections from existing stack or defaults
  const [selections, setSelections] = useState<Record<StackCategory, StackProvider>>(() => ({
    crm: stack?.crm?.provider ?? 'none',
    email: stack?.email?.provider ?? 'none',
    ads: stack?.ads?.provider ?? 'none',
    seo: stack?.seo?.provider ?? 'none',
  }));

  // Sync selections when stack loads
  const [synced, setSynced] = useState(false);
  if (stack && !synced) {
    setSynced(true);
    setSelections({
      crm: stack.crm?.provider ?? 'none',
      email: stack.email?.provider ?? 'none',
      ads: stack.ads?.provider ?? 'none',
      seo: stack.seo?.provider ?? 'none',
    });
  }

  const handleSelect = useCallback((category: StackCategory, provider: StackProvider) => {
    setSelections((prev) => ({ ...prev, [category]: provider }));
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const entries = STACK_CATEGORIES.map((cat) => ({
        category: cat.key,
        provider: selections[cat.key],
      }));
      await saveStack(entries);
      navigate(Paths.CEOX_DASHBOARD);
    } finally {
      setIsSaving(false);
    }
  }, [selections, saveStack, navigate]);

  if (isLoading) return <LoadingState message="Loading..." />;

  return (
    <CEOxStackSetupComponent
      selections={selections}
      onSelect={handleSelect}
      onSave={handleSave}
      isSaving={isSaving}
      isEdit={hasStack}
    />
  );
};

export default CEOxStackSetupContainer;
